import { MarketScopeType, RequestStatus, defaults, entities, getAPIMethod } from 'typed/api';
import { ExtractAction, ExtractPayload, IEntityActionSet, NetworkAction, action, entityActionSet, networkAction } from './utils';
import { assertNever } from 'typed/utils';

export const
    { loadAll: loadOrganizationClassification } = entityActionSet({} as entities.Entity, {
        loadAll: networkAction('LOAD_ALL_ORGANIZATION_CLASSIFICATION_TYPES', getAPIMethod('OrganizationClassificationTypeApi', 'loadAll'))
    }),

    searchOrganizationAutocompleteSlice =
        (scopeID: MarketScopeType, value: string, { countryCode, ...pagination }: { startID?: number; maxCount?: number; countryCode?: number; } = {}) => {
            switch (scopeID) {
                case MarketScopeType.FINLAND:
                    return networkAction(
                        'SEARCH_ORGANIZATION_AUTOCOMPLETE_SLICE', pagination, getAPIMethod('OrganizationApi', 'organizationAutocompleteNameGet'), value, pagination
                    );
                case MarketScopeType.GLOBAL:
                    return networkAction(
                        'SEARCH_ORGANIZATION_AUTOCOMPLETE_SLICE', pagination, getAPIMethod('OpencorporatesOrganizationApi', 'opencorporatesOrganizationAutocompleteNameGet'), value, { countryCode, ...pagination }
                    );

                default: return assertNever(scopeID, `Unknown MarketScopeType: ${scopeID}.`);
            }
        },

    resetOrganizationAutocomplete = () => action('RESET_ORGANIZATION_AUTOCOMPLETE'),


    loadOrganizationFinancialInformation =
        (financialInformationKey: string, finnishBusinessIdentifier: string, buyIfMissing = false) =>
            networkAction(
                'LOAD_ORGANIZATION_FINANCIAL_INFORMATION', { financialInformationKey }, getAPIMethod('OrganizationApi', 'organizationGetFinancialInformationGet'), finnishBusinessIdentifier, buyIfMissing
            ),

    dropOrganizationFinancialInformation = (financialInformationKey: string) => action('DROP_ORGANIZATION_FINANCIAL_INFORMATION', { financialInformationKey }),


    { loadAll: loadAllCountries } = entityActionSet(defaults.Country, { loadAll: networkAction('LOAD_ALL_COUNTRIES', getAPIMethod('CountryApi', 'loadAll')) }),

    { loadAll: loadAllMarketScopeTypes } = entityActionSet(defaults.MarketScopeType, { loadAll: networkAction('LOAD_ALL_MARKET_SCOPE_TYPES', getAPIMethod('MarketScopeTypeApi', 'loadAll')) }),

    stakeholderTypes = {
        loadAll: (options: { maxStakeholdersCount?: number; maxStakeholderCommentsCount?: number; } = {}) =>
            networkAction('LOAD_ALL_STAKEHOLDER_TYPES', { ...options }, getAPIMethod<entities.StakeholderReport[]>('StakeholderType', 'loadAll'), options),
        load: (entityID: number, options: { maxStakeholdersCount?: number; maxStakeholderCommentsCount?: number; } = {}) =>
            networkAction('LOAD_STAKEHOLDER_TYPE', { entityID, ...options }, getAPIMethod<entities.StakeholderReport>('StakeholderType', 'load'), entityID, options
            ),
        ...entityActionSet(defaults.StakeholderTypeReport, {
            add: networkAction('ADD_STAKEHOLDER_TYPE', getAPIMethod('StakeholderType', 'add')),
            edit: networkAction('EDIT_STAKEHOLDER_TYPE', getAPIMethod('StakeholderType', 'edit'))
        })
    },

    stakeholders: {
        searchSlice(stakeholderTypeID: number, pagination: { maxCommentsCount?: number; startID?: number; maxCount?: number; }): NetworkAction<'SEARCH_STAKEHOLDERS_SLICE', { parentID: number; } & typeof pagination, entities.NetworkPaginationSlice<entities.StakeholderReport>>;
        load(stakeholderTypeID: number, stakeholderID: number): IEntityActionSet<'LOAD_STAKEHOLDER', entities.StakeholderReport>['load'];
        add(stakeholderTypeID: number, stakeholder: entities.StakeholderReport): NetworkAction<'ADD_STAKEHOLDER', ExtractPayload<IEntityActionSet<'ADD_STAKEHOLDER', entities.StakeholderReport>['add']>, void>;
        add(stakeholderTypeID: number, stakeholder: entities.StakeholderReport, setupSources: boolean, sync: true): NetworkAction<'ADD_STAKEHOLDER', ExtractPayload<IEntityActionSet<'ADD_STAKEHOLDER', entities.StakeholderReport>['add']>, number>;
        add(stakeholderTypeID: number, stakeholder: entities.StakeholderReport, setupSources: boolean, sync: true, reloadAfterSync: true): NetworkAction<'ADD_STAKEHOLDER', ExtractPayload<IEntityActionSet<'ADD_STAKEHOLDER', entities.StakeholderReport>['add']>, entities.StakeholderReport>;
        edit(stakeholderTypeID: number, stakeholder: entities.StakeholderReport): NetworkAction<'EDIT_STAKEHOLDER', { entity: entities.StakeholderReport; }, void>;
        edit(stakeholderTypeID: number, stakeholder: entities.StakeholderReport, setupSources: boolean, sync: true): NetworkAction<'EDIT_STAKEHOLDER', ExtractPayload<IEntityActionSet<'ADD_STAKEHOLDER', entities.StakeholderReport>['edit']>, void>;
        edit(stakeholderTypeID: number, stakeholder: entities.StakeholderReport, setupSources: boolean, sync: true, reloadAfterSync: true): NetworkAction<'EDIT_STAKEHOLDER', ExtractPayload<IEntityActionSet<'ADD_STAKEHOLDER', entities.StakeholderReport>['edit']>, entities.StakeholderReport>;
        delete(stakeholderTypeID: number, stakeholder: entities.StakeholderReport): IEntityActionSet<'DELETE_STAKEHOLDER', entities.StakeholderReport>['delete'];
    } = {
        searchSlice: (stakeholderTypeID: number, pagination: { maxCommentsCount?: number; startID?: number; maxCount?: number; } = {}) =>
            networkAction(
                'SEARCH_STAKEHOLDERS_SLICE', { parentID: stakeholderTypeID, ...pagination }, getAPIMethod<entities.NetworkPaginationSlice<entities.StakeholderReport>>('StakeholderApi', 'stakeholderSearchSlicePut'), { TypeID: stakeholderTypeID }, pagination
            ),

        add: (stakeholderTypeID: number, stakeholder: entities.StakeholderReport, setupSources = false, sync = false, reloadAfterSync = false): any =>
            async(dispatch: any) => {
                dispatch(action('ADD_STAKEHOLDER', { parentID: stakeholderTypeID, entityID: stakeholder.ID, entity: stakeholder, status: RequestStatus.UNSYNCED }));

                if (!sync) {

                }
                else {
                    const newID = await dispatch(networkAction(
                        'ADD_STAKEHOLDER', { parentID: stakeholderTypeID, entityID: stakeholder.ID, entity: stakeholder }, getAPIMethod('StakeholderApi', 'add'), stakeholder, { setupSources }
                    ));
                    if (!reloadAfterSync) {
                        return newID;
                    }
                    else {
                        return await dispatch(stakeholders.load(stakeholderTypeID, newID!));
                    }
                }
            },
        edit: (stakeholderTypeID: number, stakeholder: entities.StakeholderReport, setupSources = false, sync = false, reloadAfterSync = false): any =>
            async(dispatch: any) => {
                dispatch(action('EDIT_STAKEHOLDER', { parentID: stakeholderTypeID, entityID: stakeholder.ID, entity: stakeholder, status: RequestStatus.UNSYNCED }));

                if (!sync) {

                }
                else {
                    await dispatch(networkAction(
                        'EDIT_STAKEHOLDER', { parentID: stakeholderTypeID, entityID: stakeholder.ID, entity: stakeholder }, getAPIMethod('StakeholderApi', 'stakeholderEditPut'), stakeholder, { setupSources }
                    ));

                    if (!reloadAfterSync) {

                    }
                    else {
                        return await dispatch(stakeholders.load(stakeholderTypeID, stakeholder.ID));
                    }
                }
            },

        ...entityActionSet(defaults.StakeholderTypeReport, defaults.StakeholderReport, {
            load: networkAction('LOAD_STAKEHOLDER', getAPIMethod('StakeholderApi', 'load')),
            delete: networkAction('DELETE_STAKEHOLDER', getAPIMethod('StakeholderApi', 'delete'))
        })
    },


    searchStakeholderCommentsSlice = (stakeholderTypeID: number, stakeholderID: number, pagination: { startID?: number; maxCount?: number; } = {}) =>
        networkAction(
            'SEARCH_STAKEHOLDER_COMMENTS_SLICE', { grandParentID: stakeholderTypeID, parentID: stakeholderID, ...pagination }, getAPIMethod('StakeholderCommentApi', 'stakeholderCommentSearchSlicePut'), { StakeholderID: stakeholderID }, pagination
        ),
    { add: addStakeholderComment } = entityActionSet(defaults.StakeholderTypeReport, defaults.StakeholderReport, defaults.StakeholderComment, {
        add: networkAction('ADD_STAKEHOLDER_COMMENT', getAPIMethod('StakeholderCommentApi', 'add'))
    }),


    { loadAll: loadAllStakeholderNewsWatchFilterLevels } = entityActionSet(defaults.StakeholderNewsWatchFilterLevel, {
        loadAll: networkAction('LOAD_ALL_STAKEHOLDER_NEWS_WATCH_FILTER_LEVELS', getAPIMethod('StakeholderNewsWatchFilterLevelApi', 'loadAll'))
    }),

    loadStakeholderNewsPreview =
        (typeID: number, stakeholderID: number, strictnessLevel: number, brandNames: string, newsSearch: boolean, webSearch: boolean, includeEnglishMarkets: boolean, maxItems: number) =>
            networkAction(
                'LOAD_STAKEHOLDER_NEWS_PREVIEW',
                { typeID, stakeholderID, strictnessLevel, brandNames, newsSearch, webSearch, includeEnglishMarkets, maxItems },
                getAPIMethod('StakeholderApi', 'stakeholderGetNewsPreviewGet'),
                stakeholderID,
                strictnessLevel,
                brandNames,
                newsSearch,
                webSearch,
                includeEnglishMarkets,
                maxItems
            ),

    dropStakeholderNewsPreview = () => action('DROP_STAKEHOLDER_NEWS_PREVIEW'),


    financialIndicators = entityActionSet(defaults.FinancialIndicator, {
        loadAll: networkAction('LOAD_ALL_FINANCIAL_INDICATORS', getAPIMethod('FinancialIndicatorApi', 'loadAll')),
        voidAll: action('VOID_ALL_FINANCIAL_INDICATORS')
    }),

    searchFinancialIndicatorData = (templates: entities.FinancialIndicatorDataFilter) =>
        networkAction(
            'SEARCH_FINANCIAL_INDICATOR_DATA', { templates }, getAPIMethod('FinancialIndicatorApi', 'financialIndicatorSearchDataReportPut'), templates
        ),

    voidFinancialIndicatorData = (indicatorID = null, filters = null, immediately = true) =>
        action('VOID_FINANCIAL_INDICATOR_DATA', { indicatorID, filters, immediately }),


    stakeholderMarketPositioningMapViewTemplates = entityActionSet(defaults.StakeholderMarketPositioningMapViewTemplate, {
        loadAll: networkAction('LOAD_ALL_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW_TEMPLATES', getAPIMethod('StakeholderMarketPositioningMapViewTemplate', 'loadAll'))
    }),

    stakeholderMarketPositioningMapViews = {
        ...entityActionSet(defaults.StakeholderMarketPositioningMapViewReport, {
            loadAll: networkAction('LOAD_ALL_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEWES', getAPIMethod('StakeholderMarketPositioningMapView', 'loadAll')),
            load: networkAction('LOAD_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW', getAPIMethod('StakeholderMarketPositioningMapView', 'load')),
            add: networkAction('ADD_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW', getAPIMethod('StakeholderMarketPositioningMapView', 'add')),
            edit: networkAction('EDIT_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW', getAPIMethod('StakeholderMarketPositioningMapView', 'edit')),
            delete: networkAction('DELETE_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW', getAPIMethod('StakeholderMarketPositioningMapView', 'delete')),
            voidAll: action('VOID_ALL_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEWS')
        }),
        invite: (viewID: number, userIDs: number[], message: string) => networkAction(
            'INVITE_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW_USERS', { entityID: viewID }, getAPIMethod('StakeholderMarketPositioningMapView', 'invite'), userIDs, viewID, { message }
        )
    },

    stakeholderMarketPositioningMapViewPositions = {
        loadAll: (parentID: number) => networkAction(
            'LOAD_ALL_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW_POSITIONS', { parentID }, getAPIMethod('StakeholderMarketPositioningMapViewPosition', 'searchSlice'), { ViewID: parentID }, {}
        ),
        ...entityActionSet(defaults.StakeholderMarketPositioningMapViewReport, defaults.StakeholderMarketPositioningMapViewPosition, {
            load: networkAction('LOAD_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW_POSITION', getAPIMethod('StakeholderMarketPositioningMapViewPosition', 'load')),
            addAll: networkAction('ADD_ALL_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW_POSITIONS', getAPIMethod('StakeholderMarketPositioningMapViewPosition', 'addAll')),
            add: networkAction('ADD_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW_POSITION', getAPIMethod('StakeholderMarketPositioningMapViewPosition', 'add')),
            editAll: networkAction('EDIT_ALL_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW_POSITIONS', getAPIMethod('StakeholderMarketPositioningMapViewPosition', 'editAll')),
            edit: networkAction('EDIT_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW_POSITION', getAPIMethod('StakeholderMarketPositioningMapViewPosition', 'edit')),
            deleteAll: networkAction('DELETE_ALL_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW_POSITIONS', getAPIMethod('StakeholderMarketPositioningMapViewPosition', 'deleteAll')),
            delete: networkAction('DELETE_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW_POSITION', getAPIMethod('StakeholderMarketPositioningMapViewPosition', 'delete')),
            voidAll: action('VOID_ALL_STAKEHOLDER_MARKET_POSITIONING_MAP_VIEW_POSITIONS')
        })
    },

    stakeholderMarketPositioningMapViewCustomItems = {
        ...entityActionSet(defaults.StakeholderMarketPositioningMapViewReport, defaults.StakeholderMarketPositioningMapViewCustomItem, {
            addAll: networkAction('ADD_ALL_MARKET_POSITIONING_MAP_VIEW_CUSTOM_ITEMS', getAPIMethod('StakeholderMarketPositioningMapViewCustomItem', 'addAll')),
            editAll: networkAction('EDIT_ALL_MARKET_POSITIONING_MAP_VIEW_CUSTOM_ITEMS', getAPIMethod('StakeholderMarketPositioningMapViewCustomItem', 'editAll')),
            deleteAll: networkAction('DELETE_ALL_MARKET_POSITIONING_MAP_VIEW_CUSTOM_ITEMS', getAPIMethod('StakeholderMarketPositioningMapViewCustomItem', 'deleteAll'))
        }),
        searchSlice: (viewID: number, pagination: { startID?: number; maxCount?: number; } = {}) => networkAction(
            'SEARCH_MARKET_POSITIONING_MAP_VIEW_CUSTOM_ITEMS_SLICE', { parentID: viewID, ...pagination }, getAPIMethod('StakeholderMarketPositioningMapViewCustomItemApi', 'stakeholderMarketPositioningMapViewCustomItemSearchSlicePut'), { ViewID: viewID }, pagination
        )
    },

    stakeholderBenchmarkTemplates = entityActionSet(defaults.StakeholderBenchmarkTemplate, {
        loadAll: networkAction('LOAD_ALL_STAKEHOLDER_BENCHMARK_TEMPLATES', getAPIMethod('StakeholderBenchmarkTemplate', 'loadAll'))
    }),

    stakeholderBenchmarks = {
        ...entityActionSet(defaults.StakeholderBenchmarkReport, {
            loadAll: networkAction('LOAD_ALL_STAKEHOLDER_BENCHMARKES', getAPIMethod('StakeholderBenchmark', 'loadAll')),
            load: networkAction('LOAD_STAKEHOLDER_BENCHMARK', getAPIMethod('StakeholderBenchmark', 'load')),
            add: networkAction('ADD_STAKEHOLDER_BENCHMARK', getAPIMethod('StakeholderBenchmark', 'add')),
            edit: networkAction('EDIT_STAKEHOLDER_BENCHMARK', getAPIMethod('StakeholderBenchmark', 'edit')),
            delete: networkAction('DELETE_STAKEHOLDER_BENCHMARK', getAPIMethod('StakeholderBenchmark', 'delete')),
            voidAll: action('VOID_ALL_STAKEHOLDER_BENCHMARKS')
        }),
        invite: (viewID: number, userIDs: number[], message: string) => networkAction(
            'INVITE_STAKEHOLDER_BENCHMARK_USERS', { entityID: viewID }, getAPIMethod('StakeholderBenchmark', 'invite'), userIDs, viewID, { message }
        )
    },

    stakeholderBenchmarkDimensions = {
        loadAll: (benchmarkID: number) => networkAction(
            'LOAD_ALL_STAKEHOLDER_BENCHMARK_DIMENSIONS', { parentID: benchmarkID }, getAPIMethod('StakeholderBenchmarkDimension', 'searchSlice'), { BenchmarkID: benchmarkID }, {}
        ),
        ...entityActionSet(defaults.StakeholderBenchmarkReport, defaults.StakeholderBenchmarkDimensionReport, {
            load: networkAction('LOAD_STAKEHOLDER_BENCHMARK_DIMENSION', getAPIMethod('StakeholderBenchmarkDimension', 'load')),
            addAll: networkAction('ADD_ALL_STAKEHOLDER_BENCHMARK_DIMENSIONS', getAPIMethod('StakeholderBenchmarkDimension', 'addAll')),
            add: networkAction('ADD_STAKEHOLDER_BENCHMARK_DIMENSION', getAPIMethod('StakeholderBenchmarkDimension', 'add')),
            editAll: networkAction('EDIT_ALL_STAKEHOLDER_BENCHMARK_DIMENSIONS', getAPIMethod('StakeholderBenchmarkDimension', 'editAll')),
            edit: networkAction('EDIT_STAKEHOLDER_BENCHMARK_DIMENSION', getAPIMethod('StakeholderBenchmarkDimension', 'edit')),
            deleteAll: networkAction('DELETE_ALL_STAKEHOLDER_BENCHMARK_DIMENSIONS', getAPIMethod('StakeholderBenchmarkDimension', 'deleteAll')),
            delete: networkAction('DELETE_STAKEHOLDER_BENCHMARK_DIMENSION', getAPIMethod('StakeholderBenchmarkDimension', 'delete')),
            voidAll: action('VOID_ALL_STAKEHOLDER_BENCHMARK_DIMENSIONS')
        })
    },

    stakeholderBenchmarkDimensionValues = {
        loadAll: (benchmarkID: number, dimensionID: number) => networkAction(
            'LOAD_ALL_STAKEHOLDER_BENCHMARK_DIMENSION_VALUES', { grandParentID: benchmarkID, parentID: dimensionID }, getAPIMethod('StakeholderBenchmarkDimensionValue', 'searchSlice'), { DimensionID: dimensionID }, {}
        ),
        ...entityActionSet(defaults.StakeholderBenchmarkReport, defaults.StakeholderBenchmarkDimensionReport, defaults.StakeholderBenchmarkDimensionValue, {
            load: networkAction('LOAD_STAKEHOLDER_BENCHMARK_DIMENSION_VALUE', getAPIMethod('StakeholderBenchmarkDimensionValue', 'load')),
            addAll: networkAction('ADD_ALL_STAKEHOLDER_BENCHMARK_DIMENSION_VALUES', getAPIMethod('StakeholderBenchmarkDimensionValue', 'addAll')),
            add: networkAction('ADD_STAKEHOLDER_BENCHMARK_DIMENSION_VALUE', getAPIMethod('StakeholderBenchmarkDimensionValue', 'add')),
            editAll: networkAction('EDIT_ALL_STAKEHOLDER_BENCHMARK_DIMENSION_VALUES', getAPIMethod('StakeholderBenchmarkDimensionValue', 'editAll')),
            edit: networkAction('EDIT_STAKEHOLDER_BENCHMARK_DIMENSION_VALUE', getAPIMethod('StakeholderBenchmarkDimensionValue', 'edit')),
            deleteAll: networkAction('DELETE_ALL_STAKEHOLDER_BENCHMARK_DIMENSION_VALUES', getAPIMethod('StakeholderBenchmarkDimensionValue', 'deleteAll')),
            delete: networkAction('DELETE_STAKEHOLDER_BENCHMARK_DIMENSION_VALUE', getAPIMethod('StakeholderBenchmarkDimensionValue', 'delete')),
            voidAll: action('VOID_ALL_STAKEHOLDER_BENCHMARK_DIMENSION_VALUES')
        })
    },

    stakeholderBenchmarkCustomItems = {
        ...entityActionSet(defaults.StakeholderBenchmarkReport, defaults.StakeholderBenchmarkCustomItem, {
            addAll: networkAction('ADD_ALL_BENCHMARK_CUSTOM_ITEMS', getAPIMethod('StakeholderBenchmarkCustomItem', 'addAll')),
            editAll: networkAction('EDIT_ALL_BENCHMARK_CUSTOM_ITEMS', getAPIMethod('StakeholderBenchmarkCustomItem', 'editAll')),
            deleteAll: networkAction('DELETE_ALL_BENCHMARK_CUSTOM_ITEMS', getAPIMethod('StakeholderBenchmarkCustomItem', 'deleteAll'))
        }),
        searchSlice: (benchmarkID: number, pagination: { startID?: number; maxCount?: number; } = {}) => networkAction(
            'SEARCH_BENCHMARK_CUSTOM_ITEMS_SLICE', { parentID: benchmarkID, ...pagination }, getAPIMethod('StakeholderBenchmarkCustomItemApi', 'stakeholderBenchmarkCustomItemSearchSlicePut'), { BenchmarkID: benchmarkID }, pagination
        )
    };


export type StakeholdersAction = ExtractAction<
| typeof loadOrganizationClassification
| typeof searchOrganizationAutocompleteSlice
| typeof resetOrganizationAutocomplete
| typeof loadOrganizationFinancialInformation
| typeof dropOrganizationFinancialInformation
| typeof loadAllCountries
| typeof loadAllMarketScopeTypes
| typeof stakeholderTypes
| typeof stakeholders.searchSlice
| typeof stakeholders
| typeof searchStakeholderCommentsSlice
| typeof addStakeholderComment
| typeof loadAllStakeholderNewsWatchFilterLevels
| typeof loadStakeholderNewsPreview
| typeof dropStakeholderNewsPreview
| typeof financialIndicators
| typeof searchFinancialIndicatorData
| typeof voidFinancialIndicatorData
| typeof stakeholderMarketPositioningMapViewTemplates
| typeof stakeholderMarketPositioningMapViews
| typeof stakeholderMarketPositioningMapViewPositions
| typeof stakeholderMarketPositioningMapViewCustomItems
| typeof stakeholderBenchmarkTemplates
| typeof stakeholderBenchmarks
| typeof stakeholderBenchmarkDimensions
| typeof stakeholderBenchmarkDimensionValues
| typeof stakeholderBenchmarkCustomItems
>;
